import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import "./UserCommonPopup.css";
import "./UserCreatePopup.css";
import axios from "axios";
import InputContainer from "../../../../components/Util/InputContainer";
import Swal from "sweetalert2";
import { getServerBaseUrl } from "../../../../utils/portConfig";
import { editUser } from "../../../../functions/Functions";

const DoctorSettingPopup = ({ isOpen, setIsOpen, onClose, isPasswordInput = true, title = "사용자 설정" }) => {
  const initialUserData = {
    id: 0,
    userid: "",
    userName: "",
    nickname: "",
    password: "",
    confirmPassword: "",
    profileImg: null,
  };

  const [userData, setUserData] = useState({});
  const [nicknameValidationMessage, setnicknameValidationMessage] = useState("");
  const [passwordValidationMessage, setPasswordValidationMessage] = useState("");
  const [newProfileImage, setNewProfileImage] = useState(null);
  const [isDeleteProfileImg, setIsDeleteProfileImg] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const API_SERVER_V2_URL = `${getServerBaseUrl()}/api/v2/user`;
  const API_SERVER_URL = `${getServerBaseUrl()}/api/v1/users`;
  const token = localStorage.getItem("token");
  const id = localStorage.getItem("id");

  // 유저정보 가져오기
  const getUser = async () => {
    try {
      const response = await axios.get(`${API_SERVER_V2_URL}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        setUserData(response.data.data);
        setImagePreview(response.data.data.profileImg ? response.data.data.profileImg.thumbnailUrl : null);
      } else {
        Swal.fire({
          icon: "error",
          title: "유저정보 조회 실패",
          text: "유저정보를 가져오는 데 실패했습니다. 다시 시도해주세요.",
        });
      }
    } catch (error) {
      console.error("Error get PatientCommunityDetailData:", error);
      if (error.response) {
        const responseData = error.response.data;
        if (responseData) {
          Swal.fire({
            icon: "error",
            title: "데이터 로딩 중 오류 발생",
            text: responseData.message,
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "서버 응답 오류",
            text: `서버 응답 오류: ${error.response.status}`,
          });
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "서버 응답이 없습니다.",
          text: "서버 응답이 없습니다.",
        });
      }
    }
  };

  // 입력값 변경 핸들러
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "nickname") {
      const noSpaceValue = value.replace(/\s/g, ""); // 공백 제거
      setUserData({
        ...userData,
        [name]: noSpaceValue,
      });
    } else if (name === "password" || name === "confirmPassword") {
      // 비밀번호 필드에서 공백 제거
      const noSpaceValue = value.replace(/\s/g, "");
      setUserData({
        ...userData,
        [name]: noSpaceValue,
      });
    } else {
      // 다른 필드는 기존 방식으로 처리
      setUserData({
        ...userData,
        [name]: value,
      });
    }
  };

  // 모달이 열렸을 때 초기화
  useEffect(() => {
    if (!isOpen) {
      setUserData({ ...initialUserData });
      setPasswordValidationMessage("");
      setnicknameValidationMessage("");
      setNewProfileImage(null);
      setImagePreview(null);
      setIsDeleteProfileImg(false);
    }
  }, [isOpen]);

  // 사용자 ID 변경 시 ID 중복 검사 메시지 초기화
  useEffect(() => {
    setnicknameValidationMessage("");
  }, [userData.nickname]);

  const handleNicknameBlur = () => {
    const nickname = userData.nickname?.trim();
    const regex = /^[가-힣a-zA-Z0-9]*$/; // 한글, 영어, 숫자만 허용

    if (!regex.test(nickname)) {
      setnicknameValidationMessage("한글, 영어, 숫자만 입력할 수 있어요.");
      return;
    } else if (nickname.length < 2 || nickname.length > 20) {
      setnicknameValidationMessage("닉네임은 2자 이상 20자 이하로 입력해주세요.");
      return;
    } else {
      setnicknameValidationMessage("");
    }
  };

  // 비밀번호 일치 여부 확인 핸들러
  const handleBlur = () => {
    if (!userData.password || !userData.confirmPassword) {
      setPasswordValidationMessage("");
      return;
    }

    if (userData.confirmPassword) {
      if (userData.password !== userData.confirmPassword) {
        setPasswordValidationMessage("비밀번호가 일치하지 않습니다.");
      } else {
        setPasswordValidationMessage("비밀번호가 일치합니다.");
      }
    } else {
      setPasswordValidationMessage("");
    }
  };

  useEffect(() => {
    if (isOpen) {
      getUser();
    }
  }, [isOpen]);

  // 유저 정보 수정
  const updateUser = async () => {
    if (nicknameValidationMessage !== "") {
      Swal.fire("알림", "닉네임을 올바르게 입력해주세요.", "warning");
      return;
    }

    if (userData.password !== userData.confirmPassword) {
      setPasswordValidationMessage("비밀번호가 일치하지 않습니다.");
      return;
    }

    if (userData.hospital === 0 && (Number(userData.auth) === 2 || Number(userData.auth) === 3)) {
      Swal.fire("알림", "소속 병원을 선택해 주세요.", "warning");
      return;
    }

    const requestBody = {
      id: id,
      userId: userData.userid,
      userName: userData.userName,
      nickname: userData.nickname,
      password: userData.password,
      profileImg: newProfileImage,
      // auth: userData.auth,
      // hospital: userData.hospital,
    };
    // Ask for confirmation before saving user edits
    const confirmationResult = await Swal.fire({
      icon: "question",
      title: "사용자 수정 저장",
      text: "사용자를 수정하시겠습니까?",
      showCancelButton: true, // Add cancel button
      confirmButtonText: "확인", // Confirm button text
      cancelButtonText: "취소", // Cancel button text
    });

    if (confirmationResult.isConfirmed) {
      try {
        if (userData.profileImg !== null && (isDeleteProfileImg || newProfileImage)) {
          await deleteUserProfile();
        }

        await editUser(API_SERVER_URL, token, { id: id }, requestBody, setIsOpen, setImagePreview);
      } catch (error) {
        console.error("Error during user update:", error);
      }
    }
  };

  // 사용자 프로필 이미지 삭제
  const removeUserProfile = () => {
    if (userData && userData.profileImg !== null) {
      setIsDeleteProfileImg(true);
    }
    setNewProfileImage(null);
    setImagePreview(null);
  };

  // 사용자 프로필 이미지 업로드
  const userProfileChange = (event) => {
    const file = event.target.files[0];
    setNewProfileImage(file);

    // 이미지 미리보기 업데이트
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    if (file) {
      reader.readAsDataURL(file);
    }
    event.target.value = "";
  };

  // 유저 프로필 이미지 삭제
  const deleteUserProfile = async () => {
    try {
      const response = await axios.delete(`${API_SERVER_URL}/${userData.id}/deleteProfileImg`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 204) {
        setNewProfileImage(null);
        setImagePreview(null);
      } else {
        await Swal.fire({
          icon: "error",
          title: "실패",
          text: "이미지 삭제 실패. 다시 시도해주세요.",
          confirmButtonText: "확인",
          customClass: {
            popup: "custom-swal-popup",
          },
        });
      }
    } catch (error) {
      console.error("Error deleting ProfieImg:", error);
      if (error.response) {
        const responseData = error.response.data;
        if (responseData) {
          Swal.fire({
            icon: "error",
            title: "이미지 삭제 오류",
            text: responseData,
            confirmButtonText: "확인",
            customClass: {
              popup: "custom-swal-popup",
            },
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "서버 응답 오류",
            text: `서버 응답 오류: ${error.response.status}`,
            customClass: {
              popup: "custom-swal-popup",
            },
          });
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "서버 응답이 없습니다.",
          text: "서버 응답이 없습니다.",
          customClass: {
            popup: "custom-swal-popup",
          },
        });
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="User Popup"
      style={{
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1000,
        },
        content: {
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          padding: "10px 20px 10px 20px",
          overflow: "hidden",
          border: "1px solid #ccc",
          background: "white",
          borderRadius: "4px",
          width: "400px",
          height: "80vh",
          maxHeight: "400px",
          overflowY: "auto",
          zIndex: 1032,
        },
      }}
    >
      <div className="modal-header-close-button" onClick={onClose}>
        x
      </div>
      <div className="user-modal-content">
        <div className="user-modal-header">
          <h2>{title}</h2>
        </div>
        <div className="input-container" style={{ marginBottom: "10px" }}>
          <div className="input-row">
            <label className="move-center">사용자 프로필</label>
            <div className="input-field move-img-container-centner">
              <div className="selected-image-preview">
                {!newProfileImage && !imagePreview && (
                  <span className="img-upload-text">
                    이미지를 업로드하세요.
                    <br />
                    권장: 650 x 650
                  </span>
                )}
                {(newProfileImage || imagePreview) && (
                  <>
                    <img src={imagePreview || URL.createObjectURL(newProfileImage)} alt="Selected Preview" />
                    <span className="remove-image-button" onClick={removeUserProfile}>
                      X
                    </span>
                  </>
                )}
              </div>
              <div className="file-upload-style">
                <input
                  id="fileInput"
                  type="file"
                  accept="images/*"
                  onChange={userProfileChange}
                  style={{ display: "none" }} // 실제 파일 업로드를 숨기기 위해 display 속성을 사용합니다.
                />
                <label htmlFor="fileInput" className="custom-file-upload">
                  <span className="upload-img-icon">+</span> 이미지 업로드
                </label>
              </div>
            </div>
          </div>
        </div>
        {/*<div style={{display:'flex', width: '100%', padding: '3px', marginBottom: '3px'}}>
          <label style={{fontSize: '10px', marginLeft: '4px', color: 'red'}}>&#8251; 권장: 정사각형의 이미지를 업로드해주세요. &#8251;</label>
        </div>*/}

        {/* 사용자 ID */}
        <div className="user-tie-container">
          <InputContainer
            autoComplete="off"
            label="사용자 ID"
            name="uid"
            value={userData.userid ?? ""}
            validationMessage={<span>&nbsp;</span>}
            inputReadonly={true}
            inputStyle={{ backgroundColor: "#f0f0f0", marginRight: "5px" }} // Readonly 스타일 적용
          />
          {/* 닉네임 */}
          <InputContainer
            autoComplete="off"
            label="사용자 닉네임"
            name="nickname"
            value={userData.nickname ?? ""}
            onChange={handleChange}
            onBlur={handleNicknameBlur}
            validationMessage={nicknameValidationMessage === "" ? <span>&nbsp;</span> : nicknameValidationMessage}
            validationColor={"red"}
            // buttonLabel="중복 확인"
            // onButtonClick={handleCheckDuplicate}
          />
        </div>

        {/* 비밀번호 */}
        {isPasswordInput && (
          <div className="user-tie-container">
            <InputContainer autoComplete="off" label="비밀번호" name="password" value={userData.password ?? ""} onChange={handleChange} validationMessage={<span>&nbsp;</span>} passwordMask={true} />
            <InputContainer
              autoComplete="off"
              label="비밀번호 확인"
              name="confirmPassword"
              value={userData.confirmPassword ?? ""}
              onChange={handleChange}
              onBlur={handleBlur}
              validationMessage={passwordValidationMessage === "" ? <span>&nbsp;</span> : passwordValidationMessage}
              validationColor={passwordValidationMessage === "비밀번호가 일치하지 않습니다." ? "red" : "green"}
              passwordMask={true}
            />
          </div>
        )}

        <div className="user-button-container">
          <button className="dashboard-content-button popup-button save-button" onClick={updateUser}>
            저장
          </button>
          <button className="dashboard-content-button red-button popup-button cancel-button" onClick={onClose}>
            취소
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DoctorSettingPopup;
